import { defineStore } from 'pinia';
import type { PlaidAccount } from '@jcss/vue-plaid-link';
import type { LinkTokenResponse } from '../common/dtos/microdeposit/link-token-response.dto';
import type { MicroDepositTableEntities } from '../common/dtos/microdeposit/micro-deposit.dto';
import usePlaidOAuth from '../composables/use-plaid-oauth';

interface MicroDepositStoreState {
  microDepositList: MicroDepositTableEntities;
  plaidPublicToken: LinkTokenResponse | null;
  isLoading: boolean;
}

export const useMicroDepositStore = defineStore('microDepositStore', {
  state: (): MicroDepositStoreState => ({
    microDepositList: {
      data: [],
      total: 0,
    },
    plaidPublicToken: null,
    isLoading: false,
  }),
  actions: {
    async fetchMicroDepositList(isSignUp = false): Promise<void> {
      const { $snapApi } = useNuxtApp();

      this.isLoading = true;

      try {
        const response = await $snapApi({
          method: 'GET',
          url: isSignUp ? 'sign-up/banks/micro-deposit' : 'identity/get-micro-deposit',
        });

        if (response.status === 200) {
          this.microDepositList.data = response.data.data;
          this.microDepositList.total = response.data.data.length;
        }
      } finally {
        this.isLoading = false;
      }
    },
    async createPlaidLinkAccount(accountId: string, isSignUp = false): Promise<void> {
      const { $snapApi } = useNuxtApp();
      const data = {
        plaidAccountID: accountId,
      };

      const response = await $snapApi({
        method: 'POST',
        url: isSignUp ? 'sign-up/plaid/link-token' : 'plaid/link-token',
        data,
      });

      if (response && response.status === 200) {
        this.plaidPublicToken = response.data;
      }
    },
    async updatePlaidAccount() {
      const { $snapApi } = useNuxtApp();
      this.isLoading = true;
      const response = await $snapApi({
        method: 'GET',
        url: 'identity/update-link-token',
      });

      if (response && response.status === 200) {
        this.plaidPublicToken = response.data;
      }
    },
    getPlaidPopUpLink(itemId: number) {
      const token = this.plaidPublicToken?.link_token ?? '';
      const { persistPlaidSession, clearPlaidSession, isOAuthResume } = usePlaidOAuth();
      const returnPath = typeof window === 'undefined' ? '/' : window.location.pathname + window.location.search;
      persistPlaidSession(token, returnPath);

      return window.Plaid.create({
        token,
        receivedRedirectUri: isOAuthResume() ? window.location.href : undefined,
        onSuccess: (publicToken, metadata) => {
          clearPlaidSession();
          this.createPlaidAccessToken(publicToken, metadata.accounts, itemId).catch();
        },
        onExit: () => {
          clearPlaidSession();
          this.isLoading = false;
        },
        onEvent: (eventName) => {
          switch (eventName) {
            case 'HANDOFF': {
              this.isLoading = true;
            }
          }
        },
      });
    },
    async createPlaidAccessToken(publicToken: string, accounts: PlaidAccount[], itemId: number) {
      const { $snapApi, $toastSuccess } = useNuxtApp();
      const data = {
        public_token: publicToken,
        accounts: accounts,
        itemId: itemId,
      };

      const response = await $snapApi({
        method: 'POST',
        url: 'identity/token',
        data,
      });

      if (response && response.status === 201) {
        $toastSuccess('Your deposits are on the way! 🎉 They should appear in your bank within 1-2 days');
      }

      this.isLoading = false;
    },
    async createFundingSource(accountId: string, isSignUp: boolean) {
      const { $snapApi } = useNuxtApp();
      this.isLoading = true;
      const data = {
        plaidAccountID: accountId,
      };
      this.isLoading = false;
      return await $snapApi({
        method: 'POST',
        url: isSignUp
          ? 'sign-up/banks/without-micro-deposit?for_fees=true'
          : 'dwolla/funding-sources/create-without-micro-deposit-plaid-bank',
        data,
      });
    },
    async fetchMicroDeposits(): Promise<void> {
      const { $snapApi } = useNuxtApp();

      this.isLoading = true;

      try {
        const response = await $snapApi({
          method: 'GET',
          url: 'sign-up/banks/micro-deposit',
        });

        if (response.status === 200) {
          this.microDepositList.data = response.data.data;
          this.microDepositList.total = response.data.data.length;
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
});
